/**
 * @file index.jsx
 * @brief Displays the searchable, filterable listing feed and the favorites view.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";
import { createApiClient } from "../../api/axios";
import useListings from "../../hooks/useListings";
import useListingFilters from "../../hooks/useListingFilters";
import { truncateText } from "../../utils/truncateText";
import { getUserDisplayName } from "../../utils/userDisplayName";
import styles from "./listing-list.module.css";

const ITEMS_PER_PAGE = 12;

function stripHtml(value = "") {
    return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function formatPrice(value, locale) {
    const amount = Number(value);

    if (Number.isNaN(amount)) {
        return `${value} €`;
    }

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
}

function ListingCard({ listing, imageUrl, locale, onToggleFavorite, t, isAuthenticated }) {
    const authorName = getUserDisplayName(listing.author?.user);
    const description = stripHtml(listing.description || "") || t("listingList.noDescription");

    return (
        <article className={styles.card}>
            <div className={styles.cardImageWrapper}>
                <div className={styles.cardImageTopRow}>
                    <span className={styles.cardDateBadge}>
                        {new Date(listing.created_at).toLocaleDateString(locale)}
                    </span>
                    {isAuthenticated && (
                    <div className={styles.favoriteOverlay}>
                        <button
                            className={`${styles.favoriteBtn} ${listing.is_favorited ? styles.favorited : ""}`}
                            onClick={(event) => onToggleFavorite(event, listing.id)}
                            aria-label={listing.is_favorited ? t("listingList.removeFromFavorites") : t("listingList.addToFavorites")}
                        >
                            {listing.is_favorited ? "❤️" : "🤍"}
                        </button>
                        <span className={styles.favoriteCount}>{listing.favorites_count}</span>
                    </div>
                    )}
                </div>

                <Link to={`/listings/${listing.id}`} className={styles.cardLink}>
                    {imageUrl ? (
                        <img src={imageUrl} alt={listing.title} className={styles.cardImage} />
                    ) : (
                        <div className={styles.cardPlaceholder}>{t("common.noImage")}</div>
                    )}
                </Link>
            </div>

            <div className={styles.cardBody}>
                <div className={styles.cardHeader}>
                    <div>
                        <Link to={`/listings/${listing.id}`} className={styles.cardTitleLink}>
                            <h3 className={styles.cardTitle} title={listing.title}>{truncateText(listing.title)}</h3>
                        </Link>
                        <p className={styles.cardDescription}>{description}</p>
                    </div>
                    <div className={styles.cardPrice}>{formatPrice(listing.price, locale)}</div>
                </div>

                <div className={styles.cardMeta}>
                    {listing.category?.name && <span className={styles.badge}>{listing.category.name}</span>}
                    {listing.location?.name && <span className={styles.badgeLocation}>{listing.location.name}</span>}
                </div>

                <div className={styles.cardFooter}>
                    <div className={styles.authorCluster}>
                        <div className={styles.authorAvatar} aria-hidden="true">{authorName.avatarInitial}</div>
                        <div className={styles.authorMeta}>
                            <Link
                                to={`/profile/${listing.author.id}`}
                                className={styles.cardAuthor}
                                onClick={(event) => event.stopPropagation()}
                            >
                                {authorName.primaryName}
                            </Link>
                            {authorName.secondaryName && (
                                <span className={styles.cardAuthorUsername}>{authorName.secondaryName}</span>
                            )}
                        </div>
                    </div>

                    <Link to={`/listings/${listing.id}`} className={styles.viewLink}>
                        {t("listingList.viewListing")}
                    </Link>
                </div>
            </div>
        </article>
    );
}

/**
 * Renders the marketplace feed with search, asynchronous filters, pagination, and favorite toggling.
 * @param {boolean} showFavorites When true, restricts the feed to the authenticated user's favorites.
 * @returns {JSX.Element} The listing grid and associated filtering controls.
 */
export default function ListingList({ showFavorites = false }) {
    const auth = useAuth();
    const { t, i18n } = useTranslation();
    const api = useMemo(() => createApiClient(auth), [auth]);
    const locale = i18n.resolvedLanguage?.startsWith("fr") ? "fr-FR" : "en-US";
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsString = searchParams.toString();
    const applyingUrlStateRef = useRef(false);
    const [urlStateReady, setUrlStateReady] = useState(false);

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [locationFilter, setLocationFilter] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [categoryLevels, setCategoryLevels] = useState([]);
    const [categoryLoading, setCategoryLoading] = useState(false);

    const { listings, setListings, imagesMap, loading, error, count, next, previous } = useListings({
        api,
        accessToken: auth.user?.access_token,
        page,
        search,
        locationFilter,
        categoryFilter,
        showFavorites,
    });
    const { loadLocations, loadCategoryOptionsByParent } = useListingFilters(api);

    const hasActiveFilters = Boolean(search.trim() || locationFilter || categoryFilter);
    const totalPages = Math.max(1, Math.ceil(count / ITEMS_PER_PAGE));

    const parsePageParam = useCallback((rawValue) => {
        const parsed = Number.parseInt(rawValue || "1", 10);
        if (!Number.isFinite(parsed) || parsed < 1) {
            return 1;
        }

        return parsed;
    }, []);

    const buildCategoryLevelsFromLeaf = useCallback(async (leafCategoryId) => {
        const rootOptions = await loadCategoryOptionsByParent(null);

        if (!leafCategoryId) {
            return {
                levels: [{ parentId: null, options: rootOptions, selected: null }],
                selected: null,
            };
        }

        const lineage = [];
        let cursor = leafCategoryId;
        const visited = new Set();

        while (cursor && !visited.has(cursor)) {
            visited.add(cursor);

            const res = await api.get(`categories/${cursor}/`);
            lineage.push(res.data);
            cursor = res.data?.parent || null;
        }

        const orderedLineage = lineage.reverse();
        const levels = [];
        let parentId = null;
        let selectedLeaf = null;

        for (const category of orderedLineage) {
            const options = await loadCategoryOptionsByParent(parentId);
            const selectedOption = options.find((option) => String(option.value) === String(category.id)) || {
                value: category.id,
                label: category.name,
                pathLabel: category.full_path || category.name,
            };

            levels.push({
                parentId,
                options,
                selected: selectedOption,
            });

            parentId = category.id;
            selectedLeaf = selectedOption;
        }

        const childOptions = await loadCategoryOptionsByParent(parentId);
        if (childOptions.length) {
            levels.push({ parentId, options: childOptions, selected: null });
        }

        if (!levels.length) {
            return {
                levels: [{ parentId: null, options: rootOptions, selected: null }],
                selected: null,
            };
        }

        return { levels, selected: selectedLeaf };
    }, [api, loadCategoryOptionsByParent]);

    const toggleFavorite = async (event, listingId) => {
        event.preventDefault();
        event.stopPropagation();

        try {
            const res = await api.post(`listings/${listingId}/toggle-favorite/`);
            const { favorited, favorites_count: favoritesCount } = res.data;

            setListings((prev) => {
                const nextListings = showFavorites
                    ? prev.filter((listing) => listing.id !== listingId)
                    : prev;

                return nextListings.map((listing) =>
                    listing.id === listingId
                        ? { ...listing, is_favorited: favorited, favorites_count: favoritesCount }
                        : listing
                );
            });
        } catch (err) {
            console.error("Favorite toggle failed", err);
        }
    };

    const handlePrevious = () => setPage((currentPage) => Math.max(currentPage - 1, 1));
    const handleNext = () => setPage((currentPage) => (next ? currentPage + 1 : currentPage));

    const clearSearch = () => {
        setSearch("");
        setPage(1);
    };

    const resetCategorySelection = useCallback((rootOptions) => {
        const fallbackOptions = rootOptions ?? categoryLevels[0]?.options ?? [];
        setCategoryLevels(
            fallbackOptions.length
                ? [{ parentId: null, options: fallbackOptions, selected: null }]
                : []
        );
        setCategoryFilter(null);
        setPage(1);
    }, [categoryLevels]);

    const clearFilters = useCallback(() => {
        setSearch("");
        setLocationFilter(null);
        resetCategorySelection();
    }, [resetCategorySelection]);

    useEffect(() => {
        let cancelled = false;

        const syncStateFromUrl = async () => {
            setCategoryLoading(true);
            applyingUrlStateRef.current = true;

            try {
                const params = new URLSearchParams(searchParamsString);
                const nextSearch = params.get("search") || "";
                const nextPage = parsePageParam(params.get("page"));
                const locationId = params.get("location");
                const categoryId = params.get("category");

                setSearch(nextSearch);
                setPage(nextPage);

                if (locationId) {
                    try {
                        const locationRes = await api.get(`locations/${locationId}/`);
                        if (!cancelled) {
                            setLocationFilter({
                                value: locationRes.data.id,
                                label: locationRes.data.name,
                            });
                        }
                    } catch (err) {
                        if (!cancelled) {
                            setLocationFilter(null);
                        }
                    }
                } else {
                    setLocationFilter(null);
                }

                try {
                    const { levels, selected } = await buildCategoryLevelsFromLeaf(categoryId);
                    if (!cancelled) {
                        setCategoryLevels(levels);
                        setCategoryFilter(selected);
                    }
                } catch (err) {
                    console.error("Failed to hydrate category filters from URL", err);
                    if (!cancelled) {
                        const rootOptions = await loadCategoryOptionsByParent(null);
                        setCategoryLevels([{ parentId: null, options: rootOptions, selected: null }]);
                        setCategoryFilter(null);
                    }
                }
            } catch (err) {
                console.error("Failed to initialize listing filters", err);
            } finally {
                if (!cancelled) {
                    setCategoryLoading(false);
                    setUrlStateReady(true);
                    // Release after state updates have propagated.
                    requestAnimationFrame(() => {
                        applyingUrlStateRef.current = false;
                    });
                }
            }
        };

        syncStateFromUrl();

        return () => {
            cancelled = true;
        };
    }, [api, buildCategoryLevelsFromLeaf, loadCategoryOptionsByParent, parsePageParam, searchParamsString]);

    useEffect(() => {
        if (!urlStateReady || applyingUrlStateRef.current) {
            return;
        }

        const nextParams = new URLSearchParams();

        if (search.trim()) {
            nextParams.set("search", search.trim());
        }
        if (page > 1) {
            nextParams.set("page", String(page));
        }
        if (locationFilter?.value) {
            nextParams.set("location", String(locationFilter.value));
        }
        if (categoryFilter?.value) {
            nextParams.set("category", String(categoryFilter.value));
        }

        if (nextParams.toString() !== searchParamsString) {
            setSearchParams(nextParams);
        }
    }, [categoryFilter, locationFilter, page, search, searchParamsString, setSearchParams, urlStateReady]);

    const handleCategoryLevelChange = useCallback(async (levelIndex, selectedValue) => {
        const currentLevel = categoryLevels[levelIndex];
        if (!currentLevel) {
            return;
        }

        const selectedOption = currentLevel.options.find((option) => option.value === selectedValue) || null;
        const nextLevels = categoryLevels
            .slice(0, levelIndex + 1)
            .map((level, idx) => (idx === levelIndex ? { ...level, selected: selectedOption } : level));

        setCategoryLevels(nextLevels);
        setPage(1);

        if (!selectedOption) {
            const deepestSelected = [...nextLevels].reverse().find((level) => level.selected)?.selected || null;
            setCategoryFilter(deepestSelected);
            return;
        }

        setCategoryFilter(selectedOption);

        try {
            const childOptions = await loadCategoryOptionsByParent(selectedOption.value);

            setCategoryLevels((prev) => {
                const truncated = prev
                    .slice(0, levelIndex + 1)
                    .map((level, idx) => (idx === levelIndex ? { ...level, selected: selectedOption } : level));

                if (!childOptions.length) {
                    return truncated;
                }

                return [
                    ...truncated,
                    {
                        parentId: selectedOption.value,
                        options: childOptions,
                        selected: null,
                    },
                ];
            });
        } catch (err) {
            console.error("Failed to load subcategories", err);
        }
    }, [categoryLevels, loadCategoryOptionsByParent]);

    const selectedCategoryPath = categoryLevels
        .filter((level) => level.selected)
        .map((level) => level.selected.label)
        .join(" > ");

    return (
        <div className={styles.page}>
            <section className={styles.controlPanel}>
                <div className={styles.filterBar}>
                    <div className={styles.searchBox}>
                        <span className={styles.searchIcon}>🔎</span>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder={showFavorites ? t("listingList.searchFavorites") : t("listingList.searchListings")}
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                                setPage(1);
                            }}
                        />
                        {search && (
                            <button className={styles.searchClear} onClick={clearSearch} aria-label={t("listingList.clearSearch")}>
                                ✕
                            </button>
                        )}
                    </div>

                    <button
                        className={`${styles.filterToggle} ${filtersOpen ? styles.filterToggleActive : ""}`}
                        onClick={() => setFiltersOpen((open) => !open)}
                        aria-expanded={filtersOpen}
                        aria-label={t("listingList.toggleFilters")}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="4" y1="6" x2="20" y2="6" />
                            <line x1="8" y1="12" x2="20" y2="12" />
                            <line x1="12" y1="18" x2="20" y2="18" />
                        </svg>
                        <span>{t("listingList.filters")}</span>
                        {hasActiveFilters && <span className={styles.filterBadge} />}
                    </button>

                    <div className={`${styles.filterSelects} ${filtersOpen ? styles.filterSelectsOpen : ""}`}>
                        <div className={styles.selectWrapper}>
                            <AsyncSelect
                                cacheOptions
                                loadOptions={loadLocations}
                                defaultOptions
                                isClearable
                                placeholder={t("listingList.location")}
                                value={locationFilter}
                                onChange={(value) => {
                                    setLocationFilter(value);
                                    setPage(1);
                                }}
                                classNamePrefix="react-select"
                            />
                        </div>

                        <div className={styles.categoryLevels}>
                            {categoryLoading ? (
                                <div className={styles.categoryLoading}>{t("listingForm.loadingCategories")}</div>
                            ) : (
                                categoryLevels.map((level, index) => (
                                    <div key={level.parentId || "root"} className={styles.categorySelectWrapper}>
                                        <select
                                            className={styles.nativeSelect}
                                            value={level.selected?.value || ""}
                                            onChange={(event) => handleCategoryLevelChange(index, event.target.value)}
                                        >
                                            <option value="">
                                                {index === 0
                                                    ? t("listingForm.selectRootCategory")
                                                    : t("listingForm.selectSubCategory", { level: index })}
                                            </option>
                                            {level.options.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))
                            )}
                        </div>

                        {hasActiveFilters && (
                            <button className={styles.clearFilters} onClick={clearFilters}>
                                {t("common.clearAll")}
                            </button>
                        )}
                    </div>
                </div>

                {hasActiveFilters && (
                    <div className={styles.activeFilters}>
                        {search && (
                            <button className={styles.filterChip} onClick={clearSearch}>
                                <span>{t("listingList.searchLabel")}: {search}</span>
                                <span aria-hidden="true">✕</span>
                            </button>
                        )}
                        {locationFilter && (
                            <button
                                className={styles.filterChip}
                                onClick={() => {
                                    setLocationFilter(null);
                                    setPage(1);
                                }}
                            >
                                <span>{t("listingList.location")}: {locationFilter.label}</span>
                                <span aria-hidden="true">✕</span>
                            </button>
                        )}
                        {categoryFilter && (
                            <button
                                className={styles.filterChip}
                                onClick={() => resetCategorySelection()}
                            >
                                <span>{t("listingList.category")}: {selectedCategoryPath || categoryFilter.pathLabel || categoryFilter.label}</span>
                                <span aria-hidden="true">✕</span>
                            </button>
                        )}
                    </div>
                )}

                {categoryFilter && selectedCategoryPath && (
                    <p className={styles.categoryPath}>
                        {t("listingForm.selectedCategoryPath", { path: selectedCategoryPath })}
                    </p>
                )}
            </section>

            <div className={styles.feedHeader}>
                <div>
                    {!loading && !error && listings.length > 0 && (
                        <p className={styles.resultCount}>{t("common.listingCount", { count })}</p>
                    )}
                    <h2 className={styles.feedTitle}>
                        {showFavorites ? t("listingList.favoritesSectionTitle") : t("listingList.marketSectionTitle")}
                    </h2>
                </div>
            </div>

            {loading ? (
                <div className={styles.grid}>
                    {[...Array(6)].map((_, index) => (
                        <div key={index} className={styles.skeletonCard}>
                            <div className={styles.skeletonImage} />
                            <div className={styles.skeletonBody}>
                                <div className={styles.skeletonTitle} />
                                <div className={styles.skeletonMeta} />
                                <div className={styles.skeletonText} />
                                <div className={styles.skeletonFooter} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : error ? (
                <div className={styles.error}>{t("listingList.error", { message: error.message })}</div>
            ) : listings.length === 0 ? (
                <div className={styles.empty}>
                    <span className={styles.emptyIcon}>🔍</span>
                    <h3 className={styles.emptyTitle}>{t("listingList.noListings")}</h3>
                    <p className={styles.emptyText}>{t("listingList.emptySummary")}</p>
                    {hasActiveFilters && (
                        <button className={styles.clearFilters} onClick={clearFilters}>{t("listingList.clearFilters")}</button>
                    )}
                </div>
            ) : (
                <>
                    <div className={styles.grid}>
                        {listings.map((listing) => (
                            <ListingCard
                                key={listing.id}
                                listing={listing}
                                imageUrl={imagesMap[listing.id]}
                                locale={locale}
                                onToggleFavorite={toggleFavorite}
                                t={t}
                                isAuthenticated={auth.isAuthenticated}
                            />
                        ))}
                    </div>

                    <div className={styles.pagination}>
                        <button
                            onClick={handlePrevious}
                            disabled={!previous}
                            className={`${styles.pageBtn} ${!previous ? styles.disabledBtn : ""}`}
                        >
                            ← {t("common.previous")}
                        </button>
                        <span className={styles.pageInfo}>{t("common.pageOf", { page, total: totalPages })}</span>
                        <button
                            onClick={handleNext}
                            disabled={!next}
                            className={`${styles.pageBtn} ${!next ? styles.disabledBtn : ""}`}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
